/**
 * ActivateFlowDialog — diálogo "Salvar e Ativar" com seleção de público.
 *
 * Comportamento por triggerType:
 *
 * - manual / scheduled → audiência OBRIGATÓRIA (sem público o fluxo não tem
 *   pra quem rodar). Ao confirmar, ativa + dispara.
 *
 * - tag_added / new_contact / webhook → audiência OPCIONAL (ele dispara por
 *   evento). Se o usuário marcar "Aplicar agora a quem já se qualifica",
 *   roda em backfill nos contatos atuais.
 */

import { useMemo, useState } from 'react';
import { trpc } from '@/lib/trpc';
import { toast } from 'sonner';
import { toUserFriendlyError } from '@/lib/error-messages';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Power,
  Users,
  Tag as TagIcon,
  Layers,
  Zap,
  Loader2,
  AlertCircle,
  CheckCircle2,
  PlayCircle,
  CalendarClock,
  UserPlus,
  Webhook,
} from 'lucide-react';
import { cn } from '@/lib/utils';

const TRIGGER_LABELS: Record<string, { label: string; icon: any; needsAudience: boolean; backfillLabel: string }> = {
  manual:       { label: 'Manual / Campanha',  icon: PlayCircle,   needsAudience: true,  backfillLabel: '' },
  scheduled:    { label: 'Agendado',            icon: CalendarClock, needsAudience: true, backfillLabel: '' },
  tag_added:    { label: 'Tag adicionada',      icon: TagIcon,       needsAudience: false, backfillLabel: 'Aplicar agora a contatos que já têm a tag' },
  new_contact:  { label: 'Novo contato',        icon: UserPlus,      needsAudience: false, backfillLabel: 'Aplicar agora aos contatos já existentes' },
  webhook:      { label: 'Webhook',             icon: Webhook,       needsAudience: false, backfillLabel: 'Aplicar agora a um público específico' },
};

interface ActivateFlowDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  flowId: number;
  flowName: string;
  triggerType: string;
  triggerValue?: string;
  /** Chamado depois que ativou — o pai grava status=active e refeicha. */
  onActivated: (info: { dispatched: number; runBackfill: boolean }) => Promise<void> | void;
}

export default function ActivateFlowDialog({
  open,
  onOpenChange,
  flowId,
  flowName,
  triggerType,
  triggerValue,
  onActivated,
}: ActivateFlowDialogProps) {
  const def = TRIGGER_LABELS[triggerType] ?? TRIGGER_LABELS.manual;
  const TriggerIcon = def.icon;

  const [selectedSegmentIds, setSelectedSegmentIds] = useState<number[]>([]);
  const [selectedTagIds, setSelectedTagIds] = useState<number[]>([]);
  const [runBackfill, setRunBackfill] = useState(false);

  const { data: segments = [] } = trpc.segments.list.useQuery() as {
    data?: Array<{ id: number; name: string }>;
  };
  const { data: tags = [] } = trpc.tags.list.useQuery() as {
    data?: Array<{ id: number; name: string }>;
  };

  // Audiência ativa apenas quando precisa OU quando o usuário escolheu fazer backfill.
  const audienceActive = def.needsAudience || runBackfill;

  const previewQuery = trpc.flows.previewAudience.useQuery(
    {
      segmentIds: selectedSegmentIds,
      tagIds: selectedTagIds,
      requireEmail: true,
    },
    {
      enabled: open && audienceActive && (selectedSegmentIds.length > 0 || selectedTagIds.length > 0),
    },
  );
  const recipientCount = previewQuery.data?.count ?? 0;

  const dispatch = trpc.flows.dispatch.useMutation();

  const summaryAudience = useMemo(() => {
    if (!audienceActive) return null;
    if (selectedSegmentIds.length === 0 && selectedTagIds.length === 0) {
      return def.needsAudience ? 'nenhuma audiência selecionada (obrigatório)' : null;
    }
    const parts: string[] = [];
    if (selectedSegmentIds.length > 0) parts.push(`${selectedSegmentIds.length} segmento${selectedSegmentIds.length === 1 ? '' : 's'}`);
    if (selectedTagIds.length > 0) parts.push(`${selectedTagIds.length} tag${selectedTagIds.length === 1 ? '' : 's'}`);
    return parts.join(' + ');
  }, [audienceActive, def.needsAudience, selectedSegmentIds.length, selectedTagIds.length]);

  const canConfirm = !def.needsAudience || (audienceActive && (selectedSegmentIds.length > 0 || selectedTagIds.length > 0));

  const handleConfirm = async () => {
    try {
      // Validação de audiência obrigatória.
      const hasAudienceSelection = selectedSegmentIds.length > 0 || selectedTagIds.length > 0;
      if (def.needsAudience && !hasAudienceSelection) {
        toast.error('Selecione ao menos um segmento ou tag.');
        return;
      }

      // 1. ATIVA primeiro (operação rápida, idempotente). Se falhar, paramos
      //    e nada de e-mail é enviado.
      await onActivated({ dispatched: 0, runBackfill });

      // 2. Só agora dispara — assim status=active fica consistente com
      //    o estado dos envios. Se o dispatch falhar, o fluxo já está
      //    ativo (correto: ele vai disparar via gatilho ou re-dispatch).
      let dispatched = 0;
      if (audienceActive && hasAudienceSelection) {
        try {
          const result = await dispatch.mutateAsync({
            flowId,
            segmentIds: selectedSegmentIds,
            tagIds: selectedTagIds,
          });
          dispatched = result.dispatched;
        } catch (dispatchErr) {
          const msg = toUserFriendlyError(dispatchErr, 'não foi possível disparar agora');
          // Fluxo já está ativo; informa o usuário que o disparo falhou
          // mas a configuração foi salva.
          toast.error(`Fluxo ativado, mas o disparo falhou: ${msg}. Tente disparar novamente.`);
          onOpenChange(false);
          return;
        }
      }

      // 3. Mensagem final.
      if (dispatched > 0) {
        toast.success(`Fluxo ativado e disparado para ${dispatched} contato${dispatched === 1 ? '' : 's'}.`);
      } else if (def.needsAudience) {
        toast.success('Fluxo ativado.');
      } else {
        toast.success(`Fluxo ativado. Vai disparar quando ${def.label.toLowerCase()} acontecer.`);
      }

      onOpenChange(false);
      setSelectedSegmentIds([]);
      setSelectedTagIds([]);
      setRunBackfill(false);
    } catch (err) {
      toast.error(toUserFriendlyError(err, 'Falha ao ativar o fluxo.'));
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="w-[95vw] max-w-lg mx-2 sm:mx-0">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Power className="w-5 h-5 text-emerald-600" />
            Ativar fluxo
          </DialogTitle>
          <DialogDescription>
            Confirme o gatilho e o público antes de ativar <strong>{flowName}</strong>.
          </DialogDescription>
        </DialogHeader>

        {/* Trigger summary */}
        <Card className="p-3 bg-indigo-50/40 border-indigo-200">
          <div className="flex items-center gap-2">
            <div className="h-7 w-7 rounded-md bg-indigo-600 flex items-center justify-center shrink-0">
              <Zap className="w-3.5 h-3.5 text-white" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] uppercase tracking-wider text-indigo-700 font-bold">Gatilho</p>
              <p className="text-sm font-medium text-indigo-900 flex items-center gap-1.5">
                <TriggerIcon className="w-3.5 h-3.5" />
                {def.label}
                {triggerValue && <span className="text-xs text-indigo-700/80 truncate">— {triggerValue}</span>}
              </p>
            </div>
          </div>
        </Card>

        {/* Audience picker */}
        <div className="space-y-3">
          {def.needsAudience ? (
            <div className="text-xs text-muted-foreground">
              Esse gatilho exige escolha de público — sem audiência o fluxo não tem pra quem rodar.
            </div>
          ) : (
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={runBackfill}
                onCheckedChange={(c) => setRunBackfill(Boolean(c))}
              />
              <span className="text-sm">{def.backfillLabel}</span>
            </label>
          )}

          {audienceActive && (
            <div className="space-y-2">
              <div className="grid grid-cols-2 gap-2">
                <MultiPicker
                  label="Segmentos"
                  icon={Layers}
                  items={segments}
                  selectedIds={selectedSegmentIds}
                  onChange={setSelectedSegmentIds}
                />
                <MultiPicker
                  label="Tags"
                  icon={TagIcon}
                  items={tags}
                  selectedIds={selectedTagIds}
                  onChange={setSelectedTagIds}
                />
              </div>

              {(selectedSegmentIds.length > 0 || selectedTagIds.length > 0) && (
                <Card className={cn(
                  'p-2.5 flex items-center gap-2',
                  recipientCount > 0
                    ? 'bg-emerald-50 border-emerald-200'
                    : 'bg-amber-50 border-amber-200',
                )}>
                  <Users className={cn('w-4 h-4', recipientCount > 0 ? 'text-emerald-700' : 'text-amber-700')} />
                  <div className="text-xs flex-1">
                    {previewQuery.isFetching ? (
                      <span className="text-muted-foreground">Calculando audiência…</span>
                    ) : (
                      <>
                        <strong className={recipientCount > 0 ? 'text-emerald-900' : 'text-amber-900'}>
                          {recipientCount} contato{recipientCount === 1 ? '' : 's'}
                        </strong>
                        <span className="text-muted-foreground ml-1">
                          (deduplicado, com e-mail, sem opt-out)
                        </span>
                      </>
                    )}
                  </div>
                </Card>
              )}
            </div>
          )}
        </div>

        {/* Resumo */}
        <div className="rounded-lg border border-emerald-200/60 bg-emerald-50/40 p-3 space-y-1 text-[12px]">
          <p className="font-semibold text-emerald-900 flex items-center gap-1.5">
            <CheckCircle2 className="w-3.5 h-3.5" />
            O que vai acontecer
          </p>
          <ul className="text-emerald-800/90 space-y-0.5 ml-5 list-disc leading-relaxed">
            <li>Status do fluxo: <strong>ativo</strong>.</li>
            {def.needsAudience ? (
              <li>Disparar agora para <strong>{recipientCount}</strong> contato{recipientCount === 1 ? '' : 's'} ({summaryAudience || '—'}).</li>
            ) : runBackfill ? (
              <li>Disparar agora para <strong>{recipientCount}</strong> contato{recipientCount === 1 ? '' : 's'} (backfill).</li>
            ) : (
              <li>Esperar <strong>{def.label.toLowerCase()}</strong> para cada contato entrar.</li>
            )}
          </ul>
        </div>

        {dispatch.isError && (
          <div className="rounded-md border border-destructive/30 bg-destructive/5 p-2.5 flex items-start gap-2">
            <AlertCircle className="w-4 h-4 text-destructive shrink-0 mt-0.5" />
            <p className="text-[11px] text-destructive leading-snug break-words">
              {dispatch.error?.message}
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button
            onClick={handleConfirm}
            disabled={!canConfirm || dispatch.isPending}
            className="gap-1.5"
          >
            {dispatch.isPending ? (
              <><Loader2 className="w-4 h-4 animate-spin" /> Ativando…</>
            ) : (
              <><Power className="w-4 h-4" /> Ativar e disparar</>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ─── Multi-Picker (segmentos / tags) ─────────────────────────────────────────

function MultiPicker({
  label,
  icon: Icon,
  items,
  selectedIds,
  onChange,
}: {
  label: string;
  icon: any;
  items: Array<{ id: number; name: string }>;
  selectedIds: number[];
  onChange: (ids: number[]) => void;
}) {
  const summary = selectedIds.length === 0
    ? `Nenhum${label === 'Tags' ? 'a' : ''} selecionad${label === 'Tags' ? 'a' : 'o'}`
    : `${selectedIds.length} selecionad${selectedIds.length === 1 ? (label === 'Tags' ? 'a' : 'o') : (label === 'Tags' ? 'as' : 'os')}`;

  const toggle = (id: number) =>
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);

  return (
    <div>
      <label className="text-xs font-medium mb-1 block">{label}</label>
      <Popover>
        <PopoverTrigger asChild>
          <Button variant="outline" size="sm" className="w-full h-9 justify-start font-normal text-xs gap-1.5">
            <Icon className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
            <span className="truncate flex-1 text-left">{summary}</span>
          </Button>
        </PopoverTrigger>
        <PopoverContent align="start" className="p-2 w-64">
          <ScrollArea className="h-56">
            {items.length === 0 ? (
              <p className="text-xs text-muted-foreground p-2 text-center">Nenhum cadastrado.</p>
            ) : (
              items.map((it) => (
                <label
                  key={it.id}
                  className="flex items-center gap-2 px-1 py-1.5 text-xs cursor-pointer hover:bg-muted rounded"
                >
                  <Checkbox checked={selectedIds.includes(it.id)} onCheckedChange={() => toggle(it.id)} />
                  <span className="flex-1 truncate">{it.name}</span>
                </label>
              ))
            )}
          </ScrollArea>
          {selectedIds.length > 0 && (
            <div className="border-t mt-1 pt-1.5 flex flex-wrap gap-1">
              {selectedIds.map((id) => {
                const it = items.find((x) => x.id === id);
                if (!it) return null;
                return (
                  <Badge key={id} variant="secondary" className="text-[10px] gap-1 max-w-[180px]">
                    <span className="truncate">{it.name}</span>
                    <button onClick={() => toggle(id)} className="hover:text-destructive">×</button>
                  </Badge>
                );
              })}
            </div>
          )}
        </PopoverContent>
      </Popover>
    </div>
  );
}
